<template>
  <aha-modal
    :title="$i18nPresenter.t('how_can_we_assist_you')"
    classesModal="modal-cancel-process modal-cancel-options"
    @hide="handleCloseModal"
  >
    <div class="cancel-subscription-options">
      <div class="option-group">
        <div
          v-if="!isAlreadyAcceptOffer"
          class="option-item"
          @click="handleGetOffer"
        >
          <div class="option-item-head">
            <div class="option-item-present">
              <i class="fas fa-gift text-lg"></i>
            </div>
            <p class="text-base-100 text-base font-bold px-5 flex-grow">
              {{ offerText }}
            </p>
            <div class="flex-shrink-0 ml-auto">
              <aha-button
                class="option-action-btn"
                variant="primary"
                size="md"
              >
                {{ $i18nPresenter.t('Get') }}
                <aha-spinner
                  v-if="isGetOfferProcessing"
                  variant="white"
                  class="ml-2"
                />
              </aha-button>
            </div>
          </div>
          <div class="option-item-body">
            <ul class="leading-snug">
              <li
                v-html="$i18nPresenter.t('your_new_expiry_date', {
                  date: newExpiredDate
                })"
              ></li>
              <li v-html="$i18nPresenter.t('no_additional_fee')"></li>
            </ul>
          </div>
        </div>
        <div
          class="option-item"
          :class="{
            paused: isActivePauseSubscription
          }"
          @click.once="handlePauseClick"
        >
          <div class="option-item-head">
            <div class="option-item-present">
              <i class="fas fa-calendar-alt" style="font-size: 17px"></i>
            </div>
            <p class="text-base-100 text-base font-bold px-5 flex-grow">
              {{ $i18nPresenter.t('pause_subscription_for_x_months', {
                x: 10
              }) }}
            </p>
            <div class="flex-shrink-0 ml-auto">
              <aha-button
                class="option-action-btn"
                variant="base"
                size="md"
              >
                {{ $i18nPresenter.t('pause') }}
              </aha-button>
            </div>
          </div>
          <div class="option-item-body">
            <ul class="leading-snug">
              <li
                v-html="$i18nPresenter.t('start_pausing_period', {
                  startDate: startPauseDate,
                  endDate: endPauseDate,
                })"
              ></li>
              <li>{{ $i18nPresenter.t('automatically_billing') }}</li>
              <li>{{ $i18nPresenter.t('unpause_anytime') }}</li>
            </ul>
          </div>
        </div>
        <div class="notify-info">
          <i class="fas fa-info-circle text-primary-90 mr-1" />
          {{ $i18nPresenter.t('feature_available_soon') }}
        </div>
      </div>
      <div class="mt-5">
        <div
          class="font-bold text-sm cursor-pointer flex items-center"
          @click="handleNextStep"
        >
          <span class="underline">{{ $i18nPresenter.t('continue_to_cancel') }}</span>
          <i class="fas fa-long-arrow-alt-right ml-1"></i>
        </div>
      </div>
    </div>
  </aha-modal>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import CancelSubscriptionGetOfferMixin from '@/mixins/cancel-subscription/GetOfferMixin'
import {
  CANCEL_SUBSCRIPTION_EVENT,
  CANCEL_SUBSCRIPTION_STEPS,
} from '@/constant/cancelSubscription'

const DATE_FORMAT = 'DD MMM YYYY'

export default {
  mixins: [CancelSubscriptionGetOfferMixin],
  props: {
    isAlreadyAcceptOffer: {
      type: Boolean,
      default: false,
    },
    planTimeEnd: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      isActivePauseSubscription: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      plan: (state) => state.plan,
    }),
    offerText() {
      if (this.isYearlyPlan) {
        return this.$i18nPresenter.t('get_x_extra_free_months', {
          x: 2,
        })
      }

      return this.$i18nPresenter.t('get_an_extra_free_month')
    },
    newExpiredDate() {
      if (!this.planTimeEnd) return null

      return this.isYearlyPlan
        ? moment(new Date(this.planTimeEnd * 1000)).locale('en').add(2, 'M').format(DATE_FORMAT)
        : moment(new Date(this.planTimeEnd * 1000)).locale('en').add(1, 'M').format(DATE_FORMAT)
    },
    startPauseDate() {
      if (!this.planTimeEnd) return null

      return moment(new Date(this.planTimeEnd * 1000)).locale('en').format(DATE_FORMAT)
    },
    endPauseDate() {
      if (!this.planTimeEnd) return null

      return moment(new Date(this.planTimeEnd * 1000)).locale('en').add(10, 'M').format(DATE_FORMAT)
    },
    isYearlyPlan() {
      return this.plan
        && this.plan.billingCycle
        && this.plan.billingCycle === 'yearly'
    },
  },
  methods: {
    handlePauseClick() {
      this.isActivePauseSubscription = true
      this._emitTrackEvent('Pause subscription for 6 months')
    },
    handleNextStep() {
      this._emitTrackEvent('Continue to cancel')
      this.$emit(CANCEL_SUBSCRIPTION_EVENT, CANCEL_SUBSCRIPTION_STEPS.CANCEL_AND_GET_FEEDBACK)
    },
    handleCloseModal() {
      this._emitTrackEvent('Close')
      this.$emit(CANCEL_SUBSCRIPTION_EVENT, CANCEL_SUBSCRIPTION_STEPS.END)
    },
    async handleGetOffer() {
      const eventAction = this.isYearlyPlan
        ? 'Get 2 extra free months'
        : 'Get an extra free month'

      this._emitTrackEvent(eventAction)
      await this.getOffer()
    },
    _emitTrackEvent(action) {
      this.$emit('trackEvent', {
        eventAction: `Cancel subscription > Select option pop-up > ${action}`,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@mixin notify-info-visible {
  display: block;
  animation: fadeIn;
  animation-duration: 1s;
}

.cancel-subscription-options {
  font-size: 1rem;
  line-height: normal;
}

.option-group {
  .option-item + .option-item {
    margin-top: 20px;
  }
}

.option-item {
  border-radius: 8px;
  cursor: pointer;
  transition: 0.5s all ease;

  &:hover {
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.3);
  }

  &.paused + .notify-info {
    @include notify-info-visible;
  }
}

.option-item-head {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid var(--secondary-20, #D3D7E1);
  border-bottom: none;
  border-radius: 8px 8px 0px 0px;
  background: var(--primary-70, #DDECFF);

  .option-item-present {
    border-radius: 50%;
    background: var(--primary-30, #ECF4FE);
    color: #525F7F;
    object-fit: cover;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  .option-action-btn {
    min-width: 67px;
    margin-top: -3px;
  }
}

.option-item-body {
  padding: 16px 20px 20px 20px;
  font-size: 1rem;
  background: #FFF;
  border-radius: 0 0 8px 8px;
  border: 1px solid var(--secondary-20, #D3D7E1);
  border-top: none;
  background: #FFF;

  ul {
    list-style-type: disc;
    padding-left: 20px;
  }
}

.notify-info {
  display: none;
  margin-top: 8px;
  font-size: 1rem;
  height: auto;
  padding: 8px 24px;
  border-radius: 4px;
  background: #F2F7FF;
}
</style>
